/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  Fragment,
  PureComponent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  ClassicEditor,
  Bold,
  Essentials,
  Italic,
  Paragraph,
  List,
  Heading,
} from 'ckeditor5';
import { CKEditor } from '@ckeditor/ckeditor5-react';

import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import axios from 'axios';
import { concat } from 'lodash';

import { Grid, Segment, Tab, Loader, Button } from 'semantic-ui-react';
import {
  evalPlanManage,
  evalManage,
  updateEvaluationPlan,
  getProjectOutcomeSummary,
  updateOutcomeChart,
  updateOutcomeChartFilter,
  saveEvaluationNarrative,
} from '../../../../store/actions';

import {
  isEmpty,
  map,
  get,
  find,
  size,
  orderBy,
} from '../../../../utils/lodash';

import { getPageDisable } from '../../../../utils';
import { ActivityOutcomeRatings } from '../../../../components/charts/ActivityOutcomeRatings';

// Same one-level-merge PATCH endpoint the Report Builder itself patches
// (ReportPreview.jsx's patchCustomisation) — called directly here rather
// than via a shared thunk since this is the only place outside the Report
// Builder that needs to write into sReportCustomisation.chartImages.
const patchChartImages = (activityId, chartImages) =>
  axios.patch(`/project/${activityId}/report-customisation`, {
    chartImages,
  });

const OUTCOME_NARRATIVE_TOOLBAR = (t) => ({
  plugins: [Essentials, Bold, Italic, List, Heading, Paragraph],
  toolbar: ['heading', '|', 'bold', 'italic', 'bulletedList', 'numberedList'],
  placeholder: t('activity.evaluation.outcome_narrative.toolbar_placeholder'),
});

import '../activity.scss';

function OutcomeAnalysisCKEditor({ initialData, onSave }) {
  const { t } = useTranslation();
  const editorRef = useRef(null);
  const initialDataRef = useRef(null);
  if (initialDataRef.current === null) {
    initialDataRef.current = initialData || '';
  }
  const editorDataRef = useRef(initialDataRef.current);
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const hasUserEditedRef = useRef(false);

  useEffect(() => {
    if (
      !hasUserEditedRef.current &&
      initialData &&
      editorRef.current &&
      initialData !== editorDataRef.current
    ) {
      editorRef.current.setData(initialData);
      initialDataRef.current = editorRef.current.getData();
      editorDataRef.current = initialDataRef.current;
    }
  }, [initialData]);

  return (
    <CKEditor
      editor={ClassicEditor}
      config={{
        plugins: [Essentials, Bold, Italic, List, Heading, Paragraph],
        toolbar: [
          'heading',
          '|',
          'bold',
          'italic',
          'bulletedList',
          'numberedList',
        ],
        placeholder: t(
          'activity.evaluation.outcome_analysis_editor_placeholder'
        ),
      }}
      data={initialDataRef.current}
      onChange={(event, editor) => {
        hasUserEditedRef.current = true;
        editorDataRef.current = editor.getData();
      }}
      onReady={(editor) => {
        editorRef.current = editor;
        const normalised = editor.getData();
        initialDataRef.current = normalised;
        editorDataRef.current = normalised;
        editor.editing.view.document.on('blur', () => {
          if (editorDataRef.current !== initialDataRef.current) {
            onSaveRef.current(editorDataRef.current);
          }
        });
      }}
    />
  );
}

// Sentinel `type`s written to reportCharts.outcomeCharts — one row per
// graph mode, so Trend and Individual can each be included independently.
const TREND_CHART_TYPE = 'TREND';
const INDIVIDUAL_CHART_TYPE = 'INDIVIDUAL';
// Holds the last selected Method/Evaluation-Plan/Multiseries filter — kept
// separate from the two above since its lifecycle isn't tied to "include in
// report" (see ActivityOutcomeRatings' reportMode/onFilterChange props).
const FILTER_CHART_TYPE = 'FILTER';

// One Yes/No "Include in report" toggle for a single graph type.
const SingleModeToggle = ({ t, label, active, isDisabled, onChange }) => (
  <div className="outcome-summary-intercept-flex-item">
    <div className="checkbox-container-include-in-report">
      <div className="include-in-report-label">{label}</div>
      <div className="button-include">
        <Button.Group
          className="btn-include-in-report"
          onClick={() => !isDisabled && onChange(!active)}
        >
          <Button active={active} disabled={isDisabled}>
            {t('actions.yes')}
          </Button>
          <Button active={!active} disabled={isDisabled}>
            {t('actions.no')}
          </Button>
        </Button.Group>
      </div>
    </div>
  </div>
);

// Similar yes/no include in report toggle, but for summary reports (aka when more than one eval plan)
const IncludeInReportToggle = ({
  outcomeId,
  currentActivity,
  updateOutcomeChart,
  user,
  isPrePost,
  chartMode,
  defaultIncluded = false,
  onReportInclusionChange,
}) => {
  const { t } = useTranslation();

  const isDisabled = getPageDisable(
    currentActivity,
    user,
    'project.edit.evaluationPlan'
  );

  const findRecord = (type) =>
    find(
      get(currentActivity, 'reportCharts.outcomeCharts', []),
      (c) => c.type === type && c.outcomeId === outcomeId
    );

  const trendHasRecord = Boolean(findRecord(TREND_CHART_TYPE));
  const individualHasRecord = Boolean(findRecord(INDIVIDUAL_CHART_TYPE));
  const [trendIncluded, setTrendIncluded] = useState(
    () => trendHasRecord || defaultIncluded
  );
  const [individualIncluded, setIndividualIncluded] = useState(
    () => individualHasRecord || defaultIncluded
  );

  const toggle = (type, status, setIncluded) => {
    setIncluded(status);
    updateOutcomeChart(currentActivity.id, {
      status,
      outcome: outcomeId,
      type,
    });
    // Drives the new Report Builder too — this legacy flag only ever fed
    // the old Reports pages (src/containers/reports/reportHome), so without
    // this the new Report Builder's "Chart not captured" placeholder never
    // clears no matter what's toggled here.
    if (onReportInclusionChange) onReportInclusionChange(status);
  };

  // Ensures filters persist and the reports tab reflects these changes straight away
  useEffect(() => {
    if (defaultIncluded && !trendHasRecord) {
      updateOutcomeChart(currentActivity.id, {
        status: true,
        outcome: outcomeId,
        type: TREND_CHART_TYPE,
      });
    }
    if (defaultIncluded && !individualHasRecord) {
      updateOutcomeChart(currentActivity.id, {
        status: true,
        outcome: outcomeId,
        type: INDIVIDUAL_CHART_TYPE,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isIndividualActive = isPrePost && chartMode === 'individual';

  return (
    <Grid className="actions-charts-container">
      <Grid.Row>
        {isIndividualActive ? (
          <SingleModeToggle
            t={t}
            label={t('report.include_in_report')}
            active={individualIncluded}
            isDisabled={isDisabled}
            onChange={(status) =>
              toggle(INDIVIDUAL_CHART_TYPE, status, setIndividualIncluded)
            }
          />
        ) : (
          <SingleModeToggle
            t={t}
            label={t('report.include_in_report')}
            active={trendIncluded}
            isDisabled={isDisabled}
            onChange={(status) =>
              toggle(TREND_CHART_TYPE, status, setTrendIncluded)
            }
          />
        )}
      </Grid.Row>
    </Grid>
  );
};

const OutcomeAnalysisSection = ({
  evalPlanSize,
  outcomeId,
  currentActivity,
  saveEvaluationNarrative,
}) => {
  const { t } = useTranslation();

  const matchingEvaluation = (() => {
    const plans = get(currentActivity, 'evaluationPlans', []);
    for (const plan of plans) {
      const eq = find(
        get(plan, 'evaluationQuestions', []),
        (eq) => eq.outcomeId === outcomeId
      );
      if (eq) return eq;
    }
    return null;
  })();

  if (!evalPlanSize || !matchingEvaluation) return null;

  return (
    <Grid className="actions-charts-container">
      <Grid.Row>
        <Grid.Column width={16}>
          <div className="individual-r">
            <div className="border-sep" />
            <div className="survey-results">{t('common.analysis')}</div>
            <div className="evaluation-comments-margin">
              <OutcomeAnalysisCKEditor
                initialData={matchingEvaluation.reportNarrative || ''}
                onSave={(text) =>
                  saveEvaluationNarrative(
                    currentActivity.id,
                    matchingEvaluation.id,
                    text
                  )
                }
              />
            </div>
          </div>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

class OutcomeSummary extends PureComponent {
  constructor(props) {
    super(props);
    this.state = { outcomeDataMap: {}, chartModeByOutcome: {} };
    // One ActivityOutcomeRatings ref per outcome, so its on-screen chart can
    // be snapshotted on demand when the (sibling) Include-in-report toggle
    // for that same outcome is clicked.
    this.aorRefs = {};
  }

  getAorRef(outcomeId) {
    if (!this.aorRefs[outcomeId]) {
      this.aorRefs[outcomeId] = React.createRef();
    }
    return this.aorRefs[outcomeId];
  }

  // All sEvaluation ids under this outcome, across every evaluation plan —
  // one outcome can be evaluated by more than one plan, and each has its
  // own chartImages entry in the Report Builder (keyed by evaluationId),
  // while this page shows a single combined chart per outcome. Same lookup
  // OutcomeAnalysisSection already uses (currentActivity.evaluationPlans[].
  // evaluationQuestions[] — evaluationQuestions is the sEvaluation alias),
  // just collecting every match instead of the first.
  getEvaluationIdsForOutcome(outcomeId) {
    const { currentActivity } = this.props;
    const ids = [];
    (get(currentActivity, 'evaluationPlans', []) || []).forEach((plan) => {
      (plan.evaluationQuestions || []).forEach((eq) => {
        if (eq.outcomeId === outcomeId) ids.push(eq.id);
      });
    });
    return ids;
  }

  // Captures (or clears) the currently-displayed chart for this outcome
  // into every evaluationId that contributes to it, so the new Report
  // Builder's per-evaluation chartImages cache reflects this toggle
  // immediately — same underlying store the Report Builder's own capture
  // path writes to (see ReportPreview.jsx's patchCustomisation).
  handleReportInclusionChange = async (outcomeId, wantInclude) => {
    const { currentActivity } = this.props;
    const evaluationIds = this.getEvaluationIdsForOutcome(outcomeId);
    if (!evaluationIds.length) return;

    if (wantInclude) {
      const aorRef = this.aorRefs[outcomeId];
      const png = aorRef?.current?.capturePng
        ? await aorRef.current.capturePng()
        : null;
      if (!png) return;
      const chartImages = {};
      evaluationIds.forEach((id) => {
        chartImages[id] = png;
      });
      await patchChartImages(currentActivity.id, chartImages);
    } else {
      const chartImages = {};
      evaluationIds.forEach((id) => {
        chartImages[id] = null;
      });
      await patchChartImages(currentActivity.id, chartImages);
    }
  };

  handleChartModeChange = (outcomeId, mode) => {
    this.setState((prev) =>
      prev.chartModeByOutcome[outcomeId] === mode
        ? prev
        : {
            chartModeByOutcome: {
              ...prev.chartModeByOutcome,
              [outcomeId]: mode,
            },
          }
    );
  };

  componentDidMount() {
    const { getProjectOutcomeSummary, currentActivity } = this.props;
    getProjectOutcomeSummary(currentActivity.id);
    this.prefetchOutcomeRatings();
  }

  componentDidUpdate(prevProps) {
    const { currentActivity } = this.props;
    if (prevProps.currentActivity.id !== currentActivity.id) {
      this.prefetchOutcomeRatings();
    }
  }

  prefetchOutcomeRatings() {
    const { currentActivity } = this.props;
    const activityId = get(currentActivity, 'id', null);
    const objectives = this.getOrderedObjectives();
    if (!activityId || isEmpty(objectives)) return;

    const outcomeIds = objectives
      .map((obj) => get(obj, 'outcome.id', null))
      .filter(Boolean);

    Promise.allSettled(
      outcomeIds.map((outcomeId) =>
        axios
          .get(`/project/${activityId}/outcome-ratings`, {
            params: { outcomeId },
          })
          .then((res) => ({ outcomeId, data: res.data }))
      )
    ).then((results) => {
      const outcomeDataMap = {};
      results.forEach((result) => {
        if (result.status === 'fulfilled') {
          const { outcomeId, data } = result.value;
          if (data?.ok) outcomeDataMap[outcomeId] = data;
        }
      });
      this.setState({ outcomeDataMap });
    });
  }

  getOrderedObjectives() {
    const { currentActivity } = this.props;

    // make sure the objectives are ordered by outcome id
    return orderBy(currentActivity.objective, ['outcome.id'], ['asc']);
  }

  renderTabs() {
    const { currentActivity, projectLoading } = this.props;
    const { outcomeDataMap, chartModeByOutcome } = this.state;
    const evalPlanSize = size(get(currentActivity, 'evaluationPlans', []));

    if (isEmpty(currentActivity.objective)) {
      return [];
    }

    const objectives = map(this.getOrderedObjectives(), (objective, i) => {
      const outcomeId = get(objective, 'outcome.id', null);
      const outcomeData = outcomeDataMap[outcomeId];

      const planCount = (() => {
        const ids = new Set();
        (outcomeData?.activities || []).forEach((a) =>
          (a.methods || []).forEach((m) =>
            (m.evaluationPlans || []).forEach((p) => ids.add(p.id))
          )
        );
        return ids.size;
      })();
      const filterRecord = find(
        get(currentActivity, 'reportCharts.outcomeCharts', []),
        (c) => c.type === FILTER_CHART_TYPE && c.outcomeId === outcomeId
      );
      const initialFilterState = filterRecord?.filterState ?? null;
      return {
        menuItem: get(objective, 'outcome.title', '-'),
        render: () => (
          <Tab.Pane key={i} menu={{ secondary: true, pointing: true }}>
            <div>
              {projectLoading ? (
                <div className="outcome-summary-loading">
                  <Loader active inline="centered" />
                </div>
              ) : (
                <div className="evaluation-outcome-summary-charts">
                  <IncludeInReportToggle
                    outcomeId={outcomeId}
                    currentActivity={currentActivity}
                    updateOutcomeChart={this.props.updateOutcomeChart}
                    user={this.props.user}
                    isPrePost={Boolean(outcomeData?.isPrePost)}
                    chartMode={chartModeByOutcome[outcomeId] || 'trend'}
                    defaultIncluded={planCount > 1}
                    onReportInclusionChange={(status) =>
                      this.handleReportInclusionChange(outcomeId, status)
                    }
                  />

                  <ActivityOutcomeRatings
                    ref={this.getAorRef(outcomeId)}
                    activityId={get(currentActivity, 'id', null)}
                    outcomeId={outcomeId}
                    prefetchedData={outcomeData ?? null}
                    showIncludeToggle={false}
                    onChartModeChange={(mode) =>
                      this.handleChartModeChange(outcomeId, mode)
                    }
                    initialFilterState={initialFilterState}
                    onFilterChange={(filterState) =>
                      this.props.updateOutcomeChartFilter(currentActivity.id, {
                        outcome: outcomeId,
                        ...filterState,
                      })
                    }
                  />

                  <OutcomeAnalysisSection
                    outcomeId={outcomeId}
                    currentActivity={currentActivity}
                    evalPlanSize={evalPlanSize === 1}
                    saveEvaluationNarrative={this.props.saveEvaluationNarrative}
                  />
                </div>
              )}
            </div>
          </Tab.Pane>
        ),
      };
    });

    // const others = [
    //   {
    //     menuItem: 'Tab ',
    //     render: () => <Tab.Pane attached={false}>Tab 2 Content</Tab.Pane>,
    //   }
    // ];

    const newArray = concat(objectives);
    return newArray;
  }

  render() {
    return (
      <Fragment>
        <Fragment>
          <div className="outcome-summary__objective-tabs">
            <Tab
              className="tabs-graph"
              menu={{ secondary: true, pointing: true }}
              panes={this.renderTabs()}
            />
          </div>
        </Fragment>
      </Fragment>
    );
  }
}

class OutcomeSummarySections extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {};
  }

  render() {
    return (
      <Segment className="survey-seg">
        <div className="survey-div">
          <OutcomeSummary {...this.props} />
        </div>
      </Segment>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    ...state.activity,
    user: state.authentication.user,
    helpTips: state.extra.activityHelpTips,
    projectLoading: state.report.projectLoading,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators(
    {
      evalPlanManage,
      evalManage,
      updateEvaluationPlan,
      getProjectOutcomeSummary,
      updateOutcomeChart,
      updateOutcomeChartFilter,
      saveEvaluationNarrative,
    },
    dispatch
  );
};

OutcomeSummarySections = connect(
  mapStateToProps,
  mapDispatchToProps
)(OutcomeSummarySections);

export { OutcomeSummarySections };
